import { useMemo } from 'react';
import { MapContainer, TileLayer } from 'react-leaflet';
import { Spinner } from '@ds';
import {
  KeepInView,
  LegMarkerKind,
  MapControls,
  MapMarker,
  MapRefSetter,
  MarkerInfoBadge,
  PositionMarker,
  TrackingPolyline,
  RouteArc,
} from '@features/components/LocationTracking';
import { TrackingStatusCard } from '@features/components/TrackingStatusCard';
import { useAppContext } from '@features/contexts/AppProvider';
import { HERE_MAPS_TILES_URL } from '@/constants/env.constants';
import { Case, CaseStatus } from '@/api/apis/case';
import { useCaseDetails } from '@/hooks/useCaseDetails';
import { useLocationTrackingQuery } from './hooks/useLocationTrackingQuery';
import { useLocationTrackingMapGeometry } from './hooks/useLocationTrackingMapGeometry';
import { useTrackingStatusCardData } from './hooks/useTrackingStatusCardData';

import 'leaflet/dist/leaflet.css';
import '@lp/features/RoutePlanner/leaflet.css';

export const LocationTrackingTab = () => {
  const { type } = useAppContext();
  const {
    caseId,
    status,
    organType,
    logisticsReference,
    donorHospitalDetails,
    donorHospitalDate,
    donorHospitalTime,
    donorHospitalTimeZone,
    txEtaHospitalDate,
    txEtaHospitalTime,
    txEtaHospitalTimeZone,
    txCenterDetails,
    organOnOCSTimestamp,
    caseName,
    recipientHospitalDate,
    recipientHospitalTime,
    recipientHospitalTimeZone,
  } = useCaseDetails() as Case;

  const { data, isLoading, error, isOnlyTxCenterProviders } = useLocationTrackingQuery({
    caseId,
    status,
    logisticsReferenceId: logisticsReference?.id,
  });

  const { map, setMap, transformedData, donorMarker, txCenterMarker, activePosition, latLngBounds } =
    useLocationTrackingMapGeometry({
      data,
      donorHospitalDetails,
      txCenterDetails,
    });

  const { cardState, title, displayData, routeProgress, isGroundLeg } = useTrackingStatusCardData({
    status,
    isOnlyTxCenterProviders,
    transformedData,
    data,
    caseFields: {
      appType: type,
      donorHospitalDate,
      donorHospitalTime,
      donorHospitalTimeZone,
      txEtaHospitalDate,
      txEtaHospitalTime,
      txEtaHospitalTimeZone,
      organOnOCSTimestamp,
      caseName,
      donorHospitalCode: donorHospitalDetails?.code,
      txCenterCode: txCenterDetails?.code,
      recipientHospitalDate,
      recipientHospitalTime,
      recipientHospitalTimeZone,
    },
  });

  const donorHoverContent = useMemo(() => {
    const value = [displayData.donorOrTime, displayData.donorOrTz].filter(Boolean).join(' ');
    return <MarkerInfoBadge label="Donor OR" value={value} />;
  }, [displayData.donorOrTime, displayData.donorOrTz]);

  const etaHoverContent = useMemo(() => {
    const value = [displayData.etaTime, displayData.etaTz].filter(Boolean).join(' ');
    return <MarkerInfoBadge label={displayData.arrivalLabel} value={value} />;
  }, [displayData.etaTime, displayData.etaTz, displayData.arrivalLabel]);

  if (isLoading) {
    return (
      <div
        className="absolute z-403 w-full h-full top-0 left-0 flex justify-center items-center"
        style={{ backgroundColor: 'rgba(246, 246, 251, 0.5)' }}
      >
        <Spinner size="md" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="absolute bottom-20 w-full z-403 flex items-center justify-center">
        <div className="p-2 max-w-100 rounded-2xl drop-shadow-md bg-white">
          <p className="text-red-500">Error loading location tracking: {error.message}</p>
        </div>
      </div>
    );
  }

  if (!donorMarker || !txCenterMarker) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">No location tracking data available</p>
      </div>
    );
  }

  return (
    <div className="relative flex-1 min-h-0 w-full h-full">
      <MapContainer
        center={donorMarker.location}
        bounds={latLngBounds ?? undefined}
        minZoom={4}
        maxZoom={17}
        className="w-full h-full relative"
        zoomControl={false}
        attributionControl={false}
        worldCopyJump
      >
        <TileLayer url={HERE_MAPS_TILES_URL} />
        <MapRefSetter onMapReady={setMap} />
        <KeepInView target={activePosition?.location ?? null} />

        <MapMarker
          id={donorMarker.id}
          position={donorMarker.location}
          hoverContent={donorHoverContent}
          showPulsation={status === CaseStatus.AtDonorHospital}
        />
        <MapMarker
          id={txCenterMarker.id}
          position={txCenterMarker.location}
          hoverContent={etaHoverContent}
          showPulsation={status === CaseStatus.AtTXCenter}
        />

        {!transformedData && <RouteArc start={donorMarker.location} end={txCenterMarker.location} />}

        {transformedData && (
          <>
            {transformedData.legMarkers.map((marker) => (
              <MapMarker
                key={marker.id}
                id={marker.id}
                position={marker.location}
                nodeType={marker.kind === LegMarkerKind.CompletedArrival ? marker.nodeType : undefined}
              />
            ))}
            {activePosition && <PositionMarker activePosition={activePosition} />}
            {transformedData.devicePolylines.map((polyline) => (
              <TrackingPolyline key={polyline.id} {...polyline} />
            ))}
          </>
        )}
      </MapContainer>
      {map && (
        <MapControls
          map={map}
          defaultCenter={donorMarker.location}
          anchor={activePosition?.location ?? null}
          resetBounds={latLngBounds}
        />
      )}
      <div className="absolute top-6 left-1/2 -translate-x-1/2 z-419">
        <TrackingStatusCard
          cardState={cardState}
          title={title}
          data={displayData}
          organType={organType}
          routeProgress={routeProgress}
          isGroundLeg={isGroundLeg}
          activePosition={activePosition}
        />
      </div>
    </div>
  );
};
